import { Link } from "react-router-dom";
import { ArrowRight, Check, MapPin, PawPrint, Heart } from "lucide-react";
import { PageHero, Reveal } from "@/components/site/Motion";

const OFFERS = [
  {
    id: "decouverte",
    name: "Séance Découverte",
    price: "65 €",
    duration: "1 h à 1 h 30 · à domicile",
    features: [
      "Écoute de vos problématiques familiales",
      "Travail concret avec l'animal",
      "Conseils personnalisés à appliquer au quotidien",
      "Idéale pour découvrir la méthode",
    ],
    highlight: false,
  },
  {
    id: "harmonie",
    name: "Pack Harmonie",
    price: "299 €",
    duration: "5 séances d'1 h à 1 h 30 · soit 59,80 € la séance",
    features: [
      "Accompagnement global de toute la famille",
      "Plan personnalisé sur plusieurs semaines",
      "Objectif : retrouver l'équilibre familial",
      "Soutien par message au quotidien inclus",
    ],
    highlight: false,
  },
  {
    id: "serenite",
    name: "Pack Sérénité",
    price: "499 €",
    duration: "10 séances d'1 h à 1 h 30 · soit 49,90 € la séance",
    features: [
      "Un vrai suivi dans la durée",
      "Bilan initial complet de la dynamique familiale",
      "Ancrage durable des nouvelles habitudes",
      "Soutien par message au quotidien inclus",
    ],
    highlight: true,
    badge: "Vrai suivi",
  },
];

export default function Prestations() {
  return (
    <div data-testid="prestations-page">
      <PageHero
        testId="prestations-hero"
        overline="Prestations & tarifs"
        lines={[
          { text: "Des accompagnements" },
          { text: "clairs et sur mesure.", accent: true },
        ]}
        intro="Chaque famille est unique : ces formules donnent un cadre, que nous ajustons ensemble dès le premier échange."
      />

      <section className="px-6 pb-24 sm:px-10 md:pb-32 lg:px-16" data-testid="prestations-grid">
        <div className="mx-auto grid max-w-7xl gap-8 lg:grid-cols-3">
          {OFFERS.map((offer, i) => (
            <Reveal key={offer.id} delay={0.1 * i} y={44}>
              <article
                className={`relative flex h-full flex-col rounded-[2rem] border p-10 transition-transform duration-300 hover:-translate-y-1.5 ${
                  offer.highlight
                    ? "border-transparent bg-terracotta text-cream shadow-[0_35px_70px_-30px_rgba(200,90,50,0.6)]"
                    : "border-stone-border bg-white/70"
                }`}
                data-testid={`prestation-card-${offer.id}`}
              >
                {offer.badge && (
                  <span className="absolute -top-4 left-8 rounded-full bg-forest px-4 py-1.5 text-xs font-bold uppercase tracking-[0.18em] text-cream" data-testid="prestation-badge">
                    {offer.badge}
                  </span>
                )}
                <h3 className={`font-serif text-2xl font-medium ${offer.highlight ? "text-cream" : "text-ink"}`}>
                  {offer.name}
                </h3>
                <p className={`mt-1 text-xs font-semibold uppercase tracking-[0.18em] ${offer.highlight ? "text-cream/70" : "text-ink/50"}`}>
                  {offer.duration}
                </p>
                <p className={`mt-6 font-serif text-6xl font-medium tracking-tight ${offer.highlight ? "text-cream" : "text-ink"}`} data-testid={`prestation-price-${offer.id}`}>
                  {offer.price}
                </p>
                <ul className="mt-8 flex-1 space-y-3.5">
                  {offer.features.map((f) => (
                    <li key={f} className="flex items-start gap-3">
                      <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${offer.highlight ? "bg-cream/20 text-cream" : "bg-terracotta/10 text-terracotta"}`}>
                        <Check size={12} strokeWidth={3} />
                      </span>
                      <span className={`text-sm leading-relaxed ${offer.highlight ? "text-cream/90" : "text-ink/70"}`}>{f}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/contact"
                  className={`mt-10 inline-flex items-center justify-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold uppercase tracking-[0.15em] transition-[background-color,color,transform] duration-300 hover:-translate-y-0.5 active:scale-95 ${
                    offer.highlight ? "bg-cream text-terracotta-deep hover:bg-white" : "bg-ink text-cream hover:bg-terracotta"
                  }`}
                  data-testid={`prestation-cta-${offer.id}`}
                >
                  Réserver <ArrowRight size={15} />
                </Link>
              </article>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.2}>
          <div className="mx-auto mt-14 max-w-3xl space-y-4" data-testid="prestations-note">
            <div className="flex items-start gap-4 rounded-3xl border border-stone-border bg-linen p-7">
              <MapPin size={20} className="mt-0.5 shrink-0 text-terracotta" />
              <p className="text-sm leading-relaxed text-ink/70">
                Déplacement inclus dans un rayon de 30 km autour de
                Muneville-le-Bingard ; au-delà, des frais kilométriques s'appliquent.
                Le premier échange (par appel ou message) est gratuit et permet de
                construire une proposition adaptée à votre situation.
              </p>
            </div>
            <div className="flex items-start gap-4 rounded-3xl border border-terracotta/30 bg-terracotta/5 p-7" data-testid="prestations-petits-budgets">
              <Heart size={20} className="mt-0.5 shrink-0 text-terracotta" />
              <p className="text-sm leading-relaxed text-ink/80">
                <span className="font-semibold text-terracotta-deep">Petits budgets ?</span>{" "}
                Contactez-moi : nous trouverons ensemble une formule adaptée à votre situation.
              </p>
            </div>
          </div>
        </Reveal>
      </section>

      {/* ÉDUCATION CANINE SEULE */}
      <section className="px-6 pb-24 sm:px-10 md:pb-32 lg:px-16" data-testid="prestations-education-canine">
        <div className="mx-auto max-w-7xl">
          <Reveal>
            <div className="relative overflow-hidden rounded-[2rem] bg-forest-deep text-cream">
              <PawPrint className="absolute -bottom-10 -right-8 h-52 w-52 rotate-45 text-cream/5" strokeWidth={1} aria-hidden="true" />
              <div className="grid items-center gap-12 p-10 md:p-14 lg:grid-cols-12">
                <div className="lg:col-span-7">
                  <p className="text-xs font-bold uppercase tracking-[0.25em] text-sand">Éducation canine seule</p>
                  <h2 className="mt-4 font-serif text-3xl font-medium leading-[1.15] md:text-4xl">
                    Besoin uniquement d'éduquer <span className="italic text-sand">votre chien ?</span>
                  </h2>
                  <p className="mt-5 max-w-xl text-sm leading-relaxed text-cream/75 md:text-base">
                    Pas d'enfant, pas besoin d'accompagnement familial global ? Je
                    propose aussi l'éducation canine à part entière, en méthodes
                    positives, à votre domicile. Titulaire de l'ACACED chien, chat,
                    NAC (août 2026).
                  </p>
                  <ul className="mt-7 grid gap-3 sm:grid-cols-2">
                    {[
                      "Séance d'1 h à 1 h 30 à votre domicile",
                      "Chiots et chiens adultes",
                      "Méthodes positives et respectueuses",
                      "Sans engagement, à la séance",
                    ].map((f) => (
                      <li key={f} className="flex items-start gap-3">
                        <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-sand/20 text-sand">
                          <Check size={12} strokeWidth={3} />
                        </span>
                        <span className="text-sm leading-relaxed text-cream/85">{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="lg:col-span-4 lg:col-start-9" data-testid="education-canine-price">
                  <p className="font-serif text-6xl font-medium tracking-tight text-cream md:text-7xl">50 €</p>
                  <p className="mt-2 text-xs font-semibold uppercase tracking-[0.2em] text-cream/60">
                    la séance de 1 h à 1 h 30 · à domicile
                  </p>
                  <Link
                    to="/contact"
                    className="mt-8 inline-flex items-center justify-center gap-2 rounded-full bg-sand px-8 py-4 text-sm font-semibold uppercase tracking-[0.15em] text-forest-deep transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-cream active:scale-95"
                    data-testid="education-canine-cta"
                  >
                    Réserver une séance <ArrowRight size={15} />
                  </Link>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
